// src/utils/api.js

const BASE_URL = process.env.REACT_APP_API_URL || "https://api.exam.rosecaregroup.co.uk";
//const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";


/**
 * Helper function to perform authenticated API requests.
 * Adds error handling and token authentication.
 *
 * @param {string} endpoint - API endpoint (e.g., "/auth/login").
 * @param {Object} options - Fetch options (method, headers, body).
 * @param {string} token - Authorization token (if required).
 * @returns {Promise<Object>} - Parsed JSON response or error.
 */
export const fetchWithAuth = async (endpoint, options = {}, token) => {
  // 1. Is the body FormData?
  const isFormData = options.body instanceof FormData;

  // 2. Build headers conditionally
  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // If NOT FormData, treat it as JSON
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
    // If body is an object, stringify it
    if (options.body && typeof options.body === "object") {
      options.body = JSON.stringify(options.body);
    }
  }

  console.log(`🌐 fetchWithAuth: ${options.method || "GET"} ${BASE_URL}${endpoint}`);
console.log("Headers:", headers);
console.log("Body:", options.body);


  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: options.method || "GET",
      ...options,
      headers,
    });

    // Check response content type
    const contentType = response.headers.get("content-type");

    if (!response.ok) {
      // If server returns JSON, parse error details
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Request failed");
      } else {
        // Otherwise, read text and throw a generic error
        const errorText = await response.text();
        console.error("🔴 API returned an error (Non-JSON Response):", errorText);
        throw new Error(`Server Error: ${response.status}`);
      }
    }

    // Return JSON if content-type is JSON, else return null
    return contentType && contentType.includes("application/json")
      ? await response.json()
      : null;
  } catch (error) {
    console.error(`🔴 API Error [${endpoint}]:`, error.message);
    throw error;
  }
};

// ==========================
// ✅ Authentication APIs
// ==========================
export const loginUser = (credentials) =>
  fetchWithAuth("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
  });

export const registerUser = async (userData, token) => {
  return fetchWithAuth(
    "/api/auth/register",
    {
      method: "POST",
      body: JSON.stringify(userData), // ✅ Ensure password is included in the request body
    },
    token
  );
};

// ==========================
// ✅ User Management APIs
// ==========================
export const fetchUsers = async (token) => {
  return fetchWithAuth("/api/auth/users", {}, token);
};

export async function fetchUserById(userId, token) {
  // We assume you have fetchWithAuth, which returns the final JSON
  return fetchWithAuth(`/api/auth/users/${userId}`, {}, token);
}

export const updateUser = (userId, userData, token) => {
  return fetchWithAuth(
    `/api/auth/users/${userId}`,
    {
      method: "PUT",
      body: {
        ...userData,
        name: `${userData.first_name || ""} ${userData.surname || ""}`, // ✅ Ensure name is included
      },
    },
    token
  );
};

export const resetUserPassword = (userId, token) => {
  return fetchWithAuth(
    "/api/auth/reset-password",
    {
      method: "POST",
      body: { userId, newPassword: "password123" },
    },
    token
  );
};

export const deleteUser = (userId, token) =>
  fetchWithAuth(
    `/api/auth/users/${userId}`,
    {
      method: "DELETE",
    },
    token
  );

// ==========================
// ✅ Exam Management APIs
// ==========================
export const fetchExams = async (token) => {
  try {
    console.log("Fetching exams...");
    const data = await fetchWithAuth("/api/exams", {}, token);
    console.log("Fetched Exams:", data);

    if (!Array.isArray(data)) {
      throw new Error("Invalid data format received!");
    }

    return data;
  } catch (error) {
    console.error("fetchExams Error:", error);
    throw error;
  }
};

export const fetchExamQuestions = async (examId, courseIds, token) => {
  try {
    console.log(`Requesting exam questions for Exam ID: ${examId}, Courses:`, courseIds);

    // Ensure courseIds is a valid array or null
    const validCourseIds =
      Array.isArray(courseIds) && courseIds.length > 0 ? courseIds.join(",") : null;

    // Only include courseIds in the query if they exist
    const queryString = validCourseIds ? `?courseIds=${validCourseIds}` : "";

    const data = await fetchWithAuth(
      `/api/exams/${examId}/questions${queryString}`,
      {},
      token
    );

    console.log("Fetched exam questions:", data);

    if (!Array.isArray(data)) {
      throw new Error("Invalid data format received!");
    }

    return data;
  } catch (error) {
    console.error("Error fetching exam questions:", error);
    throw error;
  }
};

export const updateExamQuestion = (questionId, questionData, token) =>
  fetchWithAuth(
    `/api/exams/questions/${questionId}`,
    {
      method: "PUT",
      body: questionData,
    },
    token
  );

export const deleteExamQuestion = (questionId, token) =>
  fetchWithAuth(
    `/api/exams/questions/${questionId}`,
    {
      method: "DELETE",
    },
    token
  );

export const fetchCareHomes = async (token) => {
  try {
    const response = await fetchWithAuth("/api/care_homes", {}, token);
    return response;
  } catch (error) {
    console.error("Error fetching care homes:", error);
    throw error;
  }
};

// Fetch one-third of the practice questions for a given exam and course
export const fetchPracticeQuestions = async (examId, courseId, token) => {
  return fetchWithAuth(
    `/api/exams/${examId}/practice-questions/${courseId}`,
    {},
    token
  );
};

// ==========================
// ✅ Exam Session APIs
// ==========================
export const fetchUserExamSessions = async (userId, token) => {
  console.log(`🔵 Fetching assigned exams for user ID: ${userId}`);

  try {
    const response = await fetchWithAuth(
      `/api/exam_sessions/user/${userId}`,
      { method: "GET" },
      token
    );
    console.log("🟢 Fetched Exams:", response);
    return response;
  } catch (error) {
    console.error("🔴 Error fetching exam sessions:", error);
    throw error;
  }
};

/**
 * Fetch previously saved answers for a specific exam session & user.
 * Corresponds to GET /api/exam_sessions/answers/:sessionId/:userId
 */
// src/utils/api.js
export async function fetchSessionAnswers(sessionId, userId, token) {
  try {
    // This call either returns parsed JSON OR throws an error
    const data = await fetchWithAuth(
      `/api/exam_answers/answers/${sessionId}/${userId}`,
      { method: "GET" },
      token
    );

    // 'data' is already the JSON from the server
    return data;
  } catch (error) {
    console.error("Error fetching session answers:", error);
    throw error;
  }
}


export const createExamSession = async (examSessionData, token) => {
  console.log("🔵 Sending exam session data:", JSON.stringify(examSessionData, null, 2)); // ✅ Debugging log

  return fetchWithAuth(
    "/api/exam_sessions",
    {
      method: "POST",
      body: examSessionData,
    },
    token
  );
};

export const startExamSession = async (sessionId, token) => {
  if (!sessionId || isNaN(parseInt(sessionId))) {
    console.error("❌ Invalid parameters sent to startExamSession:", { sessionId });
    throw new Error("Invalid session ID.");
  }

  try {
    console.log(`🔵 Sending API request: Start Exam - Session ID: ${sessionId}`);

    const response = await fetchWithAuth(
      `/api/exam_sessions/start/${sessionId}`,
      { method: "PUT" },
      token
    );

    return response;
  } catch (error) {
    console.error("🔴 Error starting exam:", error);
    throw error;
  }
};

/**
 * Fetch the entire session object for a given sessionId
 * e.g. { id: 123, user_id: 45, mode: "Review", status: "in_progress", ... }
 */
export async function fetchSessionData(sessionId, token) {
  return fetchWithAuth(
    `/api/exam_sessions/${sessionId}`,  // Adjust path to your actual endpoint
    { method: "GET" },
    token
  );
}

// ==========================
// ✅ 1) Create New First Attempt Answer
// ==========================
export async function submitUserAnswer(sessionId, questionId, userId, selectedAnswer, token) {
  const response = await fetchWithAuth(
    "/api/exam_answers",
    {
      method: "POST",
      body: { sessionId, questionId, userId, selectedAnswer },
    },
    token
  );

  if (!response) {
    throw new Error("Error saving answer - no response");
  }
  return response;
}

// ==========================
// ✅ 2) Check if a first attempt already exists
// ==========================
export async function checkAnswerExistsInDB(sessionId, questionId, userId, token) {
  /**
   * Backend route: GET /api/exam_answers/exists?sessionId=...&questionId=...&userId=...
   * Returns { exists: true/false }
   */
  const data = await fetchWithAuth(
    `/api/exam_answers/exists?sessionId=${sessionId}&questionId=${questionId}&userId=${userId}`,
    { method: "GET" },
    token
  );

  // Return the boolean status
  return !!data.exists;
}

// ==========================
// ✅ 3) Update an existing First Attempt
// ==========================
export async function updateUserAnswer(sessionId, questionId, userId, userAnswer, token) {
  const response = await fetchWithAuth(
    "/api/exam_answers/update",
    {
      method: "PUT",
      body: { sessionId, questionId, userId, userAnswer },
    },
    token
  );
  if (!response) {
    throw new Error("Error updating user answer");
  }
  return response;
}

// ==========================
// ✅ 4) Create or Update Second Attempt
// ==========================
export async function submitSecondAttemptAnswer(
  sessionId,
  questionId,
  userId,
  userAnswer,
  isCorrect2nd,
  token
) {
  // If your backend "POST /second_attempt" upserts, you only need this function.
  // Otherwise, see optional "updateSecondAttemptAnswer" below.
  const response = await fetchWithAuth(
    "/api/exam_answers/second_attempt",
    {
      method: "POST",
      body: { sessionId, questionId, userId, userAnswer, isCorrect2nd },
    },
    token
  );
  if (!response) {
    throw new Error("Failed to submit second-attempt answer");
  }
  return response;
}

// OPTIONAL - If your backend uses a distinct "PUT" for second attempt updates
export async function updateSecondAttemptAnswer(
  sessionId,
  questionId,
  userId,
  userAnswer,
  isCorrect2nd,
  token
) {
  const response = await fetchWithAuth(
    "/api/exam_answers/update_second_attempt",
    {
      method: "PUT",
      body: { sessionId, questionId, userId, userAnswer, isCorrect2nd },
    },
    token
  );
  if (!response) {
    throw new Error("Failed to update second attempt answer");
  }
  return response;
}

// new helper: snapshotCompletedExams
export async function snapshotCompletedExams(
  examId,
  sessionId,
  userId,              // <-- new parameter
  finalResults,
  managerExplanations,
  token
) {
  function textOfAnswer(questionObj, index) {
    if (!index) return "";
    const ansKey = `ans${index}`;
    return questionObj[ansKey] || "";
  }

  // Create the array of snapshot objects:
  const payload = finalResults.map((resObj) => {
    const q = resObj.questionData;
    const questionId = q.id;

    const firstAnswerText =
      q.type === "T"
        ? textOfAnswer(q, resObj.firstAttemptAnswer)
        : (resObj.firstAttemptAnswer || "");

    const secondAnswerText =
      q.type === "T"
        ? textOfAnswer(q, resObj.secondAttemptAnswer)
        : (resObj.secondAttemptAnswer || "");

    const correctAnswerText =
      q.type === "T"
        ? textOfAnswer(q, q.right_ans)
        : "";

    return {
      user_id: userId,                 // <-- include user_id here
      exam_session_id: sessionId,
      course_id: q.course_id,
      exam_id: examId,
      question_text: q.question,
      question_type: q.type,
      user_answer_first: firstAnswerText,
      is_correct_first: !resObj.wasInReview && !!resObj.isCorrect,
      user_answer_second: secondAnswerText,
      is_correct_second: resObj.wasInReview && !!resObj.isCorrect,
      correct_answer: correctAnswerText,
      official_explanation: q.explanation || null,
      manager_explanation: managerExplanations[questionId] || null
    };
  });

  // POST to /api/exam_sessions/bulk
  const response = await fetchWithAuth("/api/exam_sessions/bulk", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    console.error("Failed to insert snapshot to completed_exams:", response.status);
  } else {
    console.log("Successfully inserted snapshot to completed_exams!");
  }
}

export async function fetchFinalSnapshot(sessionId, token) {
  const data = await fetchWithAuth(`/api/exam_sessions/final/${sessionId}`, {}, token);
  return data;
}

export async function updateManagerExplanations(updates, token) {
  // updates = [ { id, manager_explanation }, ... ]
  return fetchWithAuth("/api/exam_sessions/explanations", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(updates)
  });
}


// ==========================
// ✅ File Upload API
// ==========================
export const uploadExamFile = async (examId, file, token) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetchWithAuth(
    `/api/exams/${examId}/upload`,
    {
      method: "POST",
      body: formData,
      // No need to manually set Content-Type because FormData
    },
    token
  );
  return response; 
};

// ==========================
// ✅ Exam Preset Management APIs
// ==========================

// 1) Fetch presets for a given exam
export const fetchPresetsByExam = (examId, token) => {
  return fetchWithAuth(`/api/exams/${examId}/presets`, {}, token);
};

// 2) Create a new preset
export const createPreset = (examId, presetName, token) => {
  return fetchWithAuth(
    `/api/exams/${examId}/presets`,
    {
      method: "POST",
      body: { preset_name: presetName },
    },
    token
  );
};

// 3) Update a preset’s name
export const updatePresetName = (presetId, presetName, token) => {
  return fetchWithAuth(
    `/api/exams/presets/${presetId}`,
    {
      method: "PUT",
      body: { preset_name: presetName },
    },
    token
  );
};

// 4) Delete a preset
export const deletePreset = (presetId, token) => {
  return fetchWithAuth(
    `/api/exams/presets/${presetId}`,
    {
      method: "DELETE",
    },
    token
  );
};

// 5) Add courses to a preset
export const addCoursesToPreset = (presetId, courseIds, token) => {
  return fetchWithAuth(
    `/api/exams/presets/${presetId}/courses`,
    {
      method: "POST",
      body: {
        course_ids: courseIds, // Must be an array
      },
    },
    token
  );
};

// 6) Remove a single course from a preset
export const removeCourseFromPreset = (presetId, courseId, token) => {
  return fetchWithAuth(
    `/api/exams/presets/${presetId}/courses/${courseId}`,
    {
      method: "DELETE",
    },
    token
  );
};

export const removeAllCoursesFromPreset = (presetId, token) => {
  return fetchWithAuth(
    `/api/exams/presets/${presetId}/courses-all`,
    {
      method: "DELETE"
    },
    token
  );
};


// src/utils/api.js

// You already have fetchWithAuth(...) somewhere. We'll assume it returns
// the *raw* fetch Response, or we just accept it as-is.

export async function fetchCompletedExams(sessionId, token) {
  // fetchWithAuth returns already-parsed data
  const data = await fetchWithAuth(
    `/api/exam_sessions/final/${sessionId}`,
    { method: "GET" },
    token
  );

  // data is plain JSON or null
  // (Optionally check if it's an array, etc.)
  if (!data) {
    throw new Error("No data returned from completed exams");
  }

  return data; // 'data' is the final rows array
}

export async function getCompletedSessions(token) {
  return await fetchWithAuth("/api/exam_sessions/completed", { method: "GET" }, token);
}

// api.js
export const fetchExamQuestionsCount = async (examId, courseId, token) => {
  const data = await fetchWithAuth(
    `/api/exam_questions/count?examId=${examId}&courseId=${courseId}`, 
    {}, 
    token
  );
  return data.count; // 'count' returned by your backend
};

// api.js
export async function fetchTotalAnswered(sessionId, token) {
  const data = await fetchWithAuth(`/api/exam_answers/count/${sessionId}`, {}, token);
  // data should be { totalAnswered: number }
  return data.totalAnswered;
}

// api.js
export async function fetchReviewProgress(sessionId, token) {
  // This returns { secondAnswered: number, totalInReview: number }
  return fetchWithAuth(`/api/exam_answers/review-progress/${sessionId}`, {}, token);
}

export const fetchPendingExamSessions = async (token) => {
  return fetchWithAuth("/api/exam_sessions/pending", { method: "GET" }, token);
};

// utils/api.js
export const deleteExamSession = (sessionId, token) => {
  return fetchWithAuth(
    `/api/exam_sessions/${sessionId}`,
    { method: "DELETE" },
    token
  );
};
